"use client"

import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"
import { FOCUS_RING } from "@/components/ui/focusRing"

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      // Separation is a dimming scrim plus the 12px backdrop blur. The scrim
      // class below is a real generated utility (globals.css mints
      // `--color-scrim` in the @theme inline block, aliasing the raw scrim
      // palette token), so it stays visible to
      // tailwindcss/no-custom-classname — unlike the
      // arbitrary-value form, and unlike the dead `bg-well/70` it replaces,
      // which never painted at all. Theme-aware: dark 4% @ 70%, light 40% @ 35%.
      //
      // z-[200], THE MODAL LAYER. A modal dialog is the topmost surface in the
      // app, above every full-screen overlay a page can raise. It was z-50, and
      // z-50 is below Cinema's lightbox (`z-[100]`, CinemaClient.tsx) and the
      // Pulse check-in (`z-[100]`), so the delete confirmation opened UNDER the
      // Cinema lightbox: an opaque `bg-background` sheet painted over it while
      // Radix's modal lock made everything else unclickable. The trash button
      // looked dead and the page looked frozen. Measured by
      // tools/cinema-delete-walk.mjs, which reads the pixels inside the confirm
      // control's own rectangle rather than its class list, because a hit test
      // could not see it: Radix sets `pointer-events: none` on the covering
      // content, so `elementFromPoint` returned the invisible button and every
      // DOM-level probe passed while the screenshot showed no dialog at all.
      //
      // The rest of the ladder: page chrome 20-50, Cinema lightbox and Pulse
      // 100, THIS 200, popover 210 and tooltip 220 (both can open INSIDE a
      // dialog, so both must sit above it), tour spotlight 8900-9000.
      "fixed inset-0 z-[200] bg-scrim backdrop-blur-md data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  showCloseButton?: boolean
}

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(({ className, children, showCloseButton = true, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        "fixed left-[50%] top-[50%] z-[200] grid w-[calc(100%-2rem)] sm:w-full max-w-lg translate-x-[-50%] translate-y-[-50%] bg-overlay backdrop-blur-xl border border-border rounded-2xl shadow-far max-h-[90vh] outline-hidden duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]",
        className
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close className={cn("absolute right-4 top-4 rounded-lg p-2 text-fg-muted hover:text-fg transition-colors disabled:pointer-events-none", FOCUS_RING)}>
          <X className="h-5 w-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
))
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "p-4 sm:p-6 border-b border-border",
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      "p-4 sm:p-6 border-t border-border flex justify-end gap-3",
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      "text-lg font-semibold text-fg leading-none tracking-tight",
      className
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-fg-secondary", className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
